import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { Bold, Italic, Heading2, Type as TypeIcon, List, ListOrdered } from 'lucide-react';
import { useEffect } from 'react';

const MiniToolbar = ({ editor }) => {
  if (!editor) return null;

  const btnClass = (active) =>
    `p-1 rounded transition-colors ${
      active
        ? 'bg-[#00D9C8]/20 text-[#00D9C8]'
        : 'text-[#A0AEC0] hover:bg-[#1A2B42] hover:text-white'
    }`;

  return (
    <div className="flex items-center gap-0.5 bg-[#0A1628] rounded-t-lg px-2 py-1" data-testid="rich-text-toolbar">
      <button
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={btnClass(editor.isActive('bold'))}
        title="Fett"
        type="button"
      >
        <Bold className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={btnClass(editor.isActive('italic'))}
        title="Kursiv"
        type="button"
      >
        <Italic className="w-3.5 h-3.5" />
      </button>
      <div className="w-px h-4 bg-[#1A2B42] mx-0.5" />
      <button
        onClick={() => editor.chain().focus().setParagraph().run()}
        className={btnClass(editor.isActive('paragraph') && !editor.isActive('heading'))}
        title="Text"
        type="button"
      >
        <TypeIcon className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={btnClass(editor.isActive('heading', { level: 3 }))}
        title="Zwischenüberschrift"
        type="button"
      >
        <Heading2 className="w-3.5 h-3.5" />
      </button>
      <div className="w-px h-4 bg-[#1A2B42] mx-0.5" />
      <button
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={btnClass(editor.isActive('bulletList'))}
        title="Liste"
        type="button"
      >
        <List className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={btnClass(editor.isActive('orderedList'))}
        title="Nummerierung"
        type="button"
      >
        <ListOrdered className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const htmlToPlainText = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const textToHtml = (text) => {
  if (!text) return '';
  if (text.startsWith('<')) return text;
  return text
    .split('\n\n')
    .map(block => {
      block = block.trim();
      if (!block) return '';
      if (block.startsWith('### ')) return `<h3>${block.slice(4)}</h3>`;
      block = block.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      block = block.replace(/\*(.+?)\*/g, '<em>$1</em>');
      return `<p>${block}</p>`;
    })
    .join('');
};

export const RichTextFieldEditor = ({ content, onChange, maxChars, placeholder, minHeight = '120px', testId }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3] },
      }),
      Placeholder.configure({
        placeholder: placeholder || '',
      }),
    ],
    content: textToHtml(content),
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'rich-field-content',
        style: `min-height: ${minHeight}`,
      },
    },
  });

  useEffect(() => {
    if (editor && content !== undefined) {
      const currentHtml = editor.getHTML();
      const newHtml = textToHtml(content);
      if (newHtml && currentHtml !== newHtml && !content.startsWith('<')) {
        editor.commands.setContent(newHtml);
      }
    }
  }, [content]);

  const plainText = editor ? htmlToPlainText(editor.getHTML()) : '';
  const charCount = plainText.length;
  const isOverLimit = maxChars && charCount > maxChars;
  const isNearLimit = maxChars && charCount > maxChars * 0.9;

  return (
    <div className="space-y-0" data-testid={testId}>
      <style>{`
        .rich-field-content {
          outline: none;
          color: white;
          font-size: 14px;
          line-height: 1.6;
          padding: 10px 12px;
        }
        .rich-field-content p { margin-bottom: 0.5em; }
        .rich-field-content h3 {
          font-size: 1.05em;
          font-weight: 600;
          color: #cbd5e1;
          margin: 0.6em 0 0.3em;
        }
        .rich-field-content strong { font-weight: 700; color: #fff; }
        .rich-field-content em { font-style: italic; color: #cbd5e1; }
        .rich-field-content ul, .rich-field-content ol {
          padding-left: 1.4em;
          margin-bottom: 0.5em;
        }
        .rich-field-content li { margin-bottom: 0.2em; }
        .rich-field-content p.is-editor-empty:first-child::before {
          color: #5A6C7D;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .rich-field-content:focus { outline: none; }
      `}</style>

      <MiniToolbar editor={editor} />

      <div className={`bg-[#1A2B42] rounded-b-lg border border-transparent focus-within:border-[#00D9C8]/40 transition-colors ${
        isOverLimit ? 'ring-2 ring-red-400' : ''
      }`}>
        <EditorContent editor={editor} />
      </div>

      {maxChars && (
        <div className={`flex items-center justify-between text-xs px-2 py-1 rounded ${
          isOverLimit ? 'bg-red-500/20 text-red-400' :
          isNearLimit ? 'bg-yellow-500/20 text-yellow-400' :
          'bg-[#0A1628] text-[#5A6C7D]'
        }`}>
          <span>{charCount.toLocaleString()} / {maxChars.toLocaleString()}</span>
          {isOverLimit && <span className="font-medium">{(charCount - maxChars).toLocaleString()} zu viel!</span>}
        </div>
      )}
    </div>
  );
};

export const richFieldHtmlToText = (html) => htmlToPlainText(html);
